import type { SheetViewProps, SheetRootProps as SilkSheetRootProps } from "@silk-hq/components";
import { createComponentId, SheetStack, Sheet as SilkSheet, VisuallyHidden } from "@silk-hq/components";
import type { Ref } from "react";

/**
 * Silk usage declaration, injected by {@link Sheet}.Root below so no other
 * file ever passes `license`. Flip to "commercial" once the commercial
 * license is purchased (silkhq.com/access) — this is the single site.
 */
const SILK_LICENSE: SilkSheetRootProps["license"] = "non-commercial";

export interface SheetRootProps extends Omit<SilkSheetRootProps, "license"> {
  ref?: Ref<HTMLDivElement>;
}

function SheetRoot(props: SheetRootProps) {
  return <SilkSheet.Root license={SILK_LICENSE} {...props} />;
}

/**
 * Silk Sheet — the engine for every traveling surface (dialogs, pages,
 * persistent sheets, action sheets).
 *
 * Parts are enumerated rather than spread from Silk so the house API is a
 * deliberate list: an upgrade that adds parts does not widen this surface
 * silently, and every part in it has a consumer.
 *
 * Anchored surfaces (menus, popovers, selects) stay on React Aria — see
 * Foundations/Overlays in Storybook for the decision tree.
 */
export const Sheet = {
  Backdrop: SilkSheet.Backdrop,
  Content: SilkSheet.Content,
  Description: SilkSheet.Description,
  Handle: SilkSheet.Handle,
  Outlet: SilkSheet.Outlet,
  Portal: SilkSheet.Portal,
  Root: SheetRoot,
  SpecialWrapper: SilkSheet.SpecialWrapper,
  Title: SilkSheet.Title,
  Trigger: SilkSheet.Trigger,
  View: SilkSheet.View,
};

export type {
  SheetBackdropProps,
  SheetContentProps,
  SheetDescriptionProps,
  SheetOutletProps,
  SheetPortalProps,
  SheetStackOutletProps,
  SheetStackRootProps,
  SheetTitleProps,
  SheetTriggerProps,
  SheetViewProps,
} from "@silk-hq/components";
export { createComponentId, SheetStack, VisuallyHidden };

/** Travel axis ("bottom" = classic bottom sheet). Not exported by Silk directly. */
export type SheetTrack = NonNullable<SheetViewProps["tracks"]>;
/** Lifecycle reported by `onTravelStatusChange`; "idleOutside" marks exit-animation completion. */
export type SheetTravelStatus = Parameters<NonNullable<SheetViewProps["onTravelStatusChange"]>>[0];
/** Detent range reported by `onTravelRangeChange` as a gesture opens and closes. */
export type SheetTravelRange = Parameters<NonNullable<SheetViewProps["onTravelRangeChange"]>>[0];
